import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Printer, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { getPublicReceipt } from '../data/receiptsRepository';
import { publicReceiptToPrintData } from '../types';
import type { ReceiptPrintFormat } from '../types';
import { ThermalReceiptPrint } from '../components/ThermalReceiptPrint';
import { LetterReceiptPrint } from '../components/LetterReceiptPrint';

/**
 * Public, unauthenticated page reached by scanning a receipt's QR code. No
 * portal needed here (unlike the admin preview) — this page IS the whole
 * document, there's no surrounding admin UI to hide from print.
 */
export function PublicReceiptPage() {
  const { token } = useParams<{ token: string }>();
  // Phones get the thermal layout by default: the 8.5in letter sheet doesn't fit.
  const [format, setFormat] = useState<ReceiptPrintFormat>(() => (window.innerWidth < 640 ? 'thermal' : 'letter'));
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ['public_receipt', token],
    queryFn: () => getPublicReceipt(token as string),
    enabled: !!token,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <Loader2 className="size-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-gray-100 px-4 text-center">
        <AlertTriangle className="size-10 text-gray-400" />
        <p className="text-gray-600">Recibo no encontrado.</p>
      </div>
    );
  }

  const printData = publicReceiptToPrintData(data, window.location.href);
  // The letter sheet is 8.5in (816px) wide; shrink it to fit narrow screens (16px gutters).
  const letterZoom = Math.min(1, (viewportWidth - 32) / 816);

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="mx-auto mb-4 flex max-w-3xl flex-col items-center gap-2 px-4 text-center print:hidden">
        <p className="text-sm font-medium text-gray-700">Asambleas de Dios de Bolivia — Verificación de recibo</p>
        {data.status === 'issued' && (
          <span className="flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800">
            <CheckCircle2 className="size-4" /> Recibo válido
          </span>
        )}
      </div>

      <div className="mx-auto mb-4 flex max-w-3xl flex-wrap items-center justify-center gap-3 px-4 print:hidden">
        <div className="flex overflow-hidden rounded-md border border-gray-300 bg-white text-sm">
          <button
            onClick={() => setFormat('thermal')}
            className={`px-3 py-1.5 ${format === 'thermal' ? 'bg-gray-900 text-white' : 'hover:bg-gray-50'}`}
          >
            Térmico
          </button>
          <button
            onClick={() => setFormat('letter')}
            className={`px-3 py-1.5 ${format === 'letter' ? 'bg-gray-900 text-white' : 'hover:bg-gray-50'}`}
          >
            Carta
          </button>
        </div>
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
        >
          <Printer className="size-4" />
          Imprimir o guardar PDF
        </button>
      </div>

      {data.status === 'void' && (
        <div className="mx-auto mb-4 w-full max-w-3xl rounded-md border border-red-300 bg-red-50 px-4 py-3 text-center text-red-800">
          <p className="text-lg font-bold tracking-wide">RECIBO ANULADO</p>
          {data.void_reason && <p className="mt-1 text-sm">Motivo: {data.void_reason}</p>}
        </div>
      )}

      <div className="flex justify-center px-4">
        <div data-receipt-zoom className="shadow-lg" style={format === 'letter' ? { zoom: letterZoom } : undefined}>
          {format === 'thermal'
            ? <ThermalReceiptPrint data={printData} />
            : <LetterReceiptPrint data={printData} />}
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: ${format === 'thermal' ? '80mm auto' : 'letter'}; margin: 0; }
          body { background: white !important; }
          [data-receipt-zoom] { zoom: 1 !important; }
        }
      `}</style>
    </div>
  );
}
